import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  handleImageUpload,
  removeImageById,
  cleanupImagePreviews,
  createImagePreviews,
  type ReviewImageFile,
  type ReviewImagePreview,
} from '@skin-seoul/service/src/utils/review/imageUtils';
import { convertHeicToJpeg, readVideoDuration } from '../../../utils/image';
import { canEncodeReviewVideo, encodeReviewVideo } from '../../../utils/video';
import { Section } from './common/SharedStyles';
import { SPACING } from './common/constants';
import {
  renderProductIntro,
  renderRatingSection,
  renderSkinInfoSection,
  renderReviewContentSection,
  renderTitleSection,
  renderImageUploadSection,
  renderRecommendationSection,
  renderAgeGroupSection,
  renderGenderSection,
  renderNicknameSection,
  renderEmailSection,
  renderTermsSection,
  renderSubmitSection,
  renderExistingMediaSection,
  reviewFieldSelector,
  ProductReviewRewardBanner,
  type PendingVideoUpload,
  type ReviewFieldName,
} from './common/formHelpers';
import { REVIEW_MEDIA_MAX_COUNT, type ReviewFormProps, type ValidationErrors } from '@skin-seoul/service';

const MobileSection = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Section style={{ marginBottom: SPACING['2xl'], ...style }}>{children}</Section>
);

/** The picker is disabled while a selection is in flight, but a change event fired before that
    landed still arrives — saying so beats dropping the files in silence. */
const MEDIA_PICKER_BUSY_MESSAGE = 'Still preparing your last selection. Please try again in a moment.';

/** Reading order, so a refused submit lands on the first thing the reviewer would have reached. */
const FIELD_ORDER: ReviewFieldName[] = [
  'rating',
  'title',
  'content',
  'images',
  'recommend',
  'skinType',
  'skinConcern',
  'ageGroup',
  'gender',
  'nickname',
  'email',
];

interface ReviewWriteFormProps extends ReviewFormProps {
  variant: 'mobile' | 'pc';
}

export const ReviewWriteForm = ({
  formData,
  formActions,
  options,
  productInfo,
  productId,
  isSubmitDisabled,
  validationErrors,
  isMember,
  onSubmit,
  variant,
  submissionAlertMessage = null,
  submissionFlaggedText,
  mode = 'create',
  isSubmitting = false,
}: ReviewWriteFormProps) => {
  const isEditing = mode === 'edit';
  const images = formData.images as ReviewImageFile[];
  // Kept and added count against one ceiling, so this follows the kept list: picking past it would
  // upload every file and then fail the submit on a count the form could have refused.
  const keptMedia = formData.keptMedia;
  const remainingAttachmentSlots = Math.max(0, REVIEW_MEDIA_MAX_COUNT - keptMedia.length);

  const handleRemoveExistingMedia = (url: string) => {
    formActions.setKeptMedia(keptMedia.filter((kept) => kept.url !== url));
  };
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [processingMessage, setProcessingMessage] = useState<string | undefined>(undefined);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [pendingVideo, setPendingVideo] = useState<PendingVideoUpload | null>(null);
  const isConvertingRef = useRef(false);
  // One clip at a time: two concurrent VideoEncoder/VideoDecoder sessions commonly fail outright on
  // mobile, and both would be driving the single pending tile.
  const encodeQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const encodeAbortRef = useRef<AbortController | null>(null);

  // Closing the form abandons the review, so the encode it was running has nothing left to finish.
  useEffect(() => () => encodeAbortRef.current?.abort(), []);

  // The poster holds the picked clip in memory until revoked. Tied to the URL rather than to the end
  // of the upload, the revoke lands when the tile stops pointing at it and never while it still does.
  const pendingPosterUrl = pendingVideo?.posterUrl ?? null;
  useEffect(() => {
    if (!pendingPosterUrl) {
      return;
    }
    return () => URL.revokeObjectURL(pendingPosterUrl);
  }, [pendingPosterUrl]);

  const encodePickedVideo = async (file: File): Promise<File> => {
    // The queue keeps draining after an abort so one failure cannot strand the clips behind it,
    // which means the clips still queued at unmount reach this point. Minting a poster for them
    // would leak it outright: `setPendingVideo` no-ops once unmounted, so the URL never reaches the
    // effect that revokes it and pins its whole File for good.
    if (encodeAbortRef.current?.signal.aborted) {
      return file;
    }

    // A clip this browser cannot encode is uploaded as it was recorded.
    if (!(await canEncodeReviewVideo())) {
      return file;
    }

    // Again, because the probe above is awaited and the form can close under it.
    if (encodeAbortRef.current?.signal.aborted) {
      return file;
    }

    // The tile belongs to the clip being encoded, not to whichever one was picked first.
    setPendingVideo({ posterUrl: URL.createObjectURL(file), progress: null });

    return encodeReviewVideo(file, {
      signal: encodeAbortRef.current?.signal,
      onProgress: (ratio) => setPendingVideo((pending) => pending && { ...pending, progress: ratio }),
    });
  };

  // `handleImageUpload` runs the picked files through one `Promise.all`, so the queue — not the call
  // order — is what keeps a second clip from starting before the first has finished.
  const enqueueVideoEncode = (file: File): Promise<File> => {
    const encoded = encodeQueueRef.current.then(() => encodePickedVideo(file));
    // Settled either way, so one clip failing to encode does not strand the ones behind it.
    encodeQueueRef.current = encoded.catch(() => undefined);
    return encoded;
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    // `handleImageUpload` appends to the `images` captured by this closure, so two overlapping runs
    // would both start from the same list and the slower one would overwrite the faster one's
    // photos. Serialise on a ref rather than `isProcessingImages`: state reads can lag behind a
    // second change event, a ref flips synchronously.
    if (isConvertingRef.current) {
      setImageUploadError(MEDIA_PICKER_BUSY_MESSAGE);
      e.target.value = '';
      return;
    }

    isConvertingRef.current = true;
    encodeAbortRef.current = new AbortController();
    setImageUploadError(null);
    setProcessingMessage(undefined);
    setIsProcessingImages(true);

    try {
      await handleImageUpload(e, images, formActions.setImages as (images: ReviewImageFile[]) => void, {
        convertHeicToJpeg,
        onError: setImageUploadError,
        maxImages: remainingAttachmentSlots,
        readVideoDuration,
        encodeVideo: enqueueVideoEncode,
      });
    } finally {
      isConvertingRef.current = false;
      setIsProcessingImages(false);
      setPendingVideo(null);
    }
  };

  const handleRemoveImage = (id: string) => {
    removeImageById(id, images, formActions.setImages as (images: ReviewImageFile[]) => void);
  };

  // Nothing is wrong with a form the reviewer has not finished filling in yet, so the messages wait
  // for the click that was actually refused.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const visibleErrors: ValidationErrors = hasAttemptedSubmit ? (validationErrors ?? {}) : {};
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToFirstError = (errors: ValidationErrors) => {
    const firstErroredField = FIELD_ORDER.find((field) => errors[field]);
    if (!firstErroredField) {
      return;
    }
    // The form scrolls inside the modal, not the page, so the section takes its own container with it.
    formRef.current
      ?.querySelector(reviewFieldSelector(firstErroredField))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Clicking is the reviewer asking what is left, so an incomplete form answers rather than sitting
  // dead. `isSubmitDisabled` is the form's own verdict; nothing here relaxes it.
  const handleSubmitAttempt = () => {
    if (!isSubmitDisabled) {
      onSubmit();
      return;
    }

    setHasAttemptedSubmit(true);
    scrollToFirstError(validationErrors ?? {});
  };

  const imagePreviews = useMemo(() => createImagePreviews(images), [images]);

  // Every change mints a fresh object URL per attachment. Left alone the dropped ones pin their File
  // for the document's lifetime, and on the orders page this form never unmounts to collect them.
  const previousPreviewsRef = useRef<ReviewImagePreview[]>([]);

  useEffect(() => {
    const liveUrls = new Set(imagePreviews.map((preview) => preview.url));
    cleanupImagePreviews(previousPreviewsRef.current.map(({ url }) => url).filter((url) => !liveUrls.has(url)));
    previousPreviewsRef.current = imagePreviews;
  }, [imagePreviews]);

  useEffect(() => () => cleanupImagePreviews(previousPreviewsRef.current.map(({ url }) => url)), []);

  const SectionComponent = variant === 'mobile' ? MobileSection : undefined;

  return (
    <div data-testid="review-write-form" ref={formRef}>
      {/* The credits were already promised — and possibly paid — when the review was created, so an
          edit does not repeat the offer. */}
      {!isEditing && (
        <div className="w-full py-4">
          <ProductReviewRewardBanner productId={productId} />
        </div>
      )}
      {renderProductIntro({ productInfo })}
      <div className="w-full h-px bg-neutral-100 shrink-0 my-4 tablet:my-8" />
      <div className="flex flex-col gap-10 tablet:gap-6 px-[2px] tablet:px-0 items-start w-full">
        {renderRatingSection({
          rating: formData.rating,
          setRating: formActions.setRating,
          SectionComponent,
          errorMessage: visibleErrors.rating,
        })}
        {renderTitleSection({
          title: formData.title,
          setTitle: formActions.setTitle,
          errorMessage: visibleErrors.title,
        })}
        {renderReviewContentSection({
          content: formData.content,
          setContent: formActions.setContent,
          alertMessage: submissionAlertMessage,
          flaggedText: submissionFlaggedText,
          errorMessage: visibleErrors.content,
        })}
        {isEditing &&
          keptMedia.length > 0 &&
          renderExistingMediaSection({
            media: keptMedia,
            onRemove: handleRemoveExistingMedia,
            isProcessingImages,
          })}
        {renderImageUploadSection({
          images,
          imagePreviews,
          handleImageChange,
          handleRemoveImage,
          isProcessingImages,
          imageUploadError,
          maxAttachments: remainingAttachmentSlots,
          processingMessage,
          pendingVideo,
          errorMessage: visibleErrors.images,
        })}
        {!isEditing &&
          renderRecommendationSection({
            recommend: formData.recommend,
            setRecommend: formActions.setRecommend,
            errorMessage: visibleErrors.recommend,
          })}
        {renderSkinInfoSection({
          skinType: formData.skinType,
          setSkinType: formActions.setSkinType,
          skinConcern: formData.skinConcern,
          setSkinConcern: formActions.setSkinConcern,
          skinTypeOptions: options.skinTypeOptions,
          skinConcernOptions: options.skinConcernOptions,
          skinTypeError: visibleErrors.skinType,
          skinConcernError: visibleErrors.skinConcern,
        })}
        {/* Demographics and identity are fixed when the review is created — the update endpoint
            accepts neither, so an edit hides them instead of showing controls that change nothing. */}
        {!isEditing && (
          <>
            {/* Figma pairs the two demographics fields on one desktop row and stacks them on mobile. */}
            <div className="flex flex-col tablet:flex-row gap-10 tablet:gap-4 w-full">
              {renderAgeGroupSection({
                ageGroup: formData.ageGroup,
                setAgeGroup: formActions.setAgeGroup,
                errorMessage: visibleErrors.ageGroup,
              })}
              {renderGenderSection({
                gender: formData.gender,
                setGender: formActions.setGender,
                errorMessage: visibleErrors.gender,
              })}
            </div>
            {/* Members keep seeing the identity they post under — hiding it made the fields vanish
                mid-form when a guest signed in. */}
            {renderNicknameSection({
              nickname: formData.nickname,
              setNickname: formActions.setNickname,
              isReadOnly: isMember,
              errorMessage: visibleErrors.nickname,
            })}
            {renderEmailSection({
              email: formData.email,
              setEmail: formActions.setEmail,
              isReadOnly: isMember,
              errorMessage: visibleErrors.email,
            })}
          </>
        )}
        {renderTermsSection()}
        {/* Disabled only where a click could not mean anything: a submission already in flight, and
            attachments still converting — photos reach `formData.images` when conversion resolves,
            so submitting under it would post the review without the one just picked. An incomplete
            form stays clickable and answers with its errors. */}
        {renderSubmitSection({
          isSubmitDisabled: isProcessingImages || isSubmitting,
          onSubmit: handleSubmitAttempt,
          // Attachments upload under this button; on mobile data an unchanged label reads as dead.
          label: isSubmitting ? 'Uploading…' : isEditing ? 'Save changes' : 'Submit review',
        })}
      </div>
    </div>
  );
};
